import * as DialogPrimitive from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import {
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTran } from "@/hooks";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import { dashboardService } from "../services/dashboard.service";
import { buildDashboardScopePayload } from "../utils/dashboard-request-scope";
import {
  buildFrameworkLabelByCode,
  resolveSelectedReportingPeriod,
} from "../utils/reporting-years";
import MetricsDrawer from "./metrics-drawer/MetricsDrawer";
import {
  buildCategorySummary,
  filterAssignedMetrics,
  normalizeAssignedMetrics,
} from "./metrics-drawer/metrics-drawer.utils";

const buildScopePayload = (filters, orgTree, reportingYears) => {
  const reportingPeriod = resolveSelectedReportingPeriod(
    filters.years,
    reportingYears,
  );
  return {
    years: filters.years || [],
    frameworks: filters.frameworks || [],
    categories: filters.categories || [],
    ...(reportingPeriod || {}),
    ...buildDashboardScopePayload({ filters, orgTree }),
  };
};

const EMPTY_METRICS = [];

const DataEntryCompletionDrawer = observer(({ open, onOpenChange }) => {
  const { t } = useTran();
  const { filters, available } = useDashboardFiltersStore();
  const [assignedMetrics, setAssignedMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [search, setSearch] = useState("");

  const orgTree = available.orgTree || [];
  const shouldWaitForOrgTree =
    open && (filters.orgIds?.length || 0) > 0 && orgTree.length === 0;

  const scopePayload = buildScopePayload(
    filters,
    orgTree,
    available.reportingYears,
  );
  const scopeSignature = JSON.stringify(scopePayload);
  const stableScopePayload = useMemo(
    () => JSON.parse(scopeSignature),
    [scopeSignature],
  );
  const frameworkLabelByCode = useMemo(
    () => buildFrameworkLabelByCode(available.frameworkObjects),
    [available.frameworkObjects],
  );
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setError("");
    setAssignedMetrics(EMPTY_METRICS);
  }, [open, scopeSignature]);

  useEffect(() => {
    if (!open || shouldWaitForOrgTree) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");

    dashboardService
      .getAssignedMetricsCompletion(stableScopePayload)
      .then((response) => {
        if (cancelled) return;
        setAssignedMetrics(
          Array.isArray(response?.assignedMetrics)
            ? response.assignedMetrics
            : EMPTY_METRICS,
        );
      })
      .catch((requestError) => {
        if (cancelled) return;
        setAssignedMetrics(EMPTY_METRICS);
        setError(
          requestError?.message ||
            t(
              "Unable to load data entry completion metrics for the current scope.",
            ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, reloadToken, shouldWaitForOrgTree, stableScopePayload, t]);

  const normalizedMetrics = useMemo(
    () => normalizeAssignedMetrics(assignedMetrics),
    [assignedMetrics],
  );
  const completedMetrics = useMemo(
    () => normalizedMetrics.filter((metric) => metric.isCompleted),
    [normalizedMetrics],
  );
  const missingMetrics = useMemo(
    () => normalizedMetrics.filter((metric) => !metric.isCompleted),
    [normalizedMetrics],
  );
  const categorySummary = useMemo(
    () => buildCategorySummary(normalizedMetrics),
    [normalizedMetrics],
  );

  const filteredMetrics = useMemo(
    () => filterAssignedMetrics(normalizedMetrics, deferredSearch),
    [deferredSearch, normalizedMetrics],
  );
  const filteredCompletedMetrics = useMemo(
    () => filteredMetrics.filter((metric) => metric.isCompleted),
    [filteredMetrics],
  );
  const filteredMissingMetrics = useMemo(
    () => filteredMetrics.filter((metric) => !metric.isCompleted),
    [filteredMetrics],
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="fixed inset-y-0 right-0 z-[80] flex h-[100dvh] w-full max-w-[min(100vw,720px)] flex-col overflow-hidden border-l border-border/70 bg-background shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right-8 data-[state=open]:duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-200"
        >
          <MetricsDrawer
            assignedMetrics={normalizedMetrics}
            completedMetrics={completedMetrics}
            missingMetrics={missingMetrics}
            filteredCompletedMetrics={filteredCompletedMetrics}
            filteredMissingMetrics={filteredMissingMetrics}
            categorySummary={categorySummary}
            loading={loading}
            error={error}
            search={search}
            onSearchChange={(value) => {
              startTransition(() => {
                setSearch(value);
              });
            }}
            onRetry={() => setReloadToken((value) => value + 1)}
            variant="data"
            frameworkLabelByCode={frameworkLabelByCode}
            t={t}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

export default DataEntryCompletionDrawer;
